import React from "react";
import { Grid, withStyles } from "@material-ui/core";
import ZipDownloadView from "./ZipDownloadView";
import downloadSuccess from "../../../../../../assets/study/zipDownloadIcon.svg";
import toolTipIcon from "../../../../../../assets/study/questionMarkTooltip.svg";

const documentDownloadProps = {
  // datafield where file file id exists in the table which is used to get file location
  fileLocationColumn: "data_file_uuid",
  // datafield where file format exists in the table
  fileFormatColumn: "data_file_format",
  // datafield where file name exists
  fileNameColumn: "data_file_name",

  // Case 1: Logged in and granted access
  toolTipTextFileDownload:
    "Click to download a copy of this file if you have been approved by dbGaP",
  iconFileDownload: downloadSuccess,

  // Case 2: Not logged in or access not granted; Icon is the same but tooltip text is different
  iconUnauthenticated: downloadSuccess,
  toolTipTextUnauthenticated:
    "You must be logged in and must already have been granted access to download a copy of this file",

  toolTipIcon,
};

const missingZipTooltip =
  "No ZIP file is available for download for this study.";

const AvailableDownloads = ({
  classes,
  zipFileData = [],
  participantFileTypes = [],
  studyAccession = "",
}) => {
  /**
   * Add "Collection" suffix to file type if not already present.
   * Prevents duplication if the file type already ends with "Collection".
   *
   * @param {string} fileType - The data_file_type value
   * @param {boolean} isLowercase - Whether to add lowercase "collection"
   * @returns {string} - File type with Collection suffix
   */
  const addCollectionSuffix = (fileType, isLowercase = false) => {
    const trimmed = fileType.trim();
    const words = trimmed.split(/\s+/);
    const lastWord = words[words.length - 1];

    // Check if last word is already "Collection" (case-insensitive)
    if (lastWord.toLowerCase() === "collection") {
      return trimmed;
    }

    // Add Collection or collection suffix
    const suffix = isLowercase ? "collection" : "Collection";
    return `${trimmed} ${suffix}`;
  };

  /**
   * Check if a file type should have a download button:
   * 1. The study must have this file type (in participantFileTypes)
   * 2. A megazip file must exist for this type in zipFileData with valid data
   */
  const hasValidDownloadForType = (dataFileType) => {
    // Check 1: Study must have this file type
    const studyHasFileType = participantFileTypes.includes(dataFileType);
    if (!studyHasFileType) return false;

    // Check 2: Megazip must exist with valid required fields
    // getZipFileForType already validates required fields
    const zipFile = getZipFileForType(dataFileType);
    return zipFile !== null;
  };

  /**
   * Find the first zip file for a given data_file_type from the studyZipFileQuery response
   * Validates that required fields exist before returning
   */
  const getZipFileForType = (dataFileType) => {
    const entry = zipFileData.find(
      (item) => item.data_file_type === dataFileType,
    );
    if (!entry || !entry.zip_files || entry.zip_files.length === 0) return null;

    const zipFile = entry.zip_files[0];

    // Validate required field exists and is not null/undefined
    if (!zipFile.data_file_uuid) {
      return null;
    }

    return zipFile;
  };

  // Generate buttons dynamically for all valid file types
  const allButtons = participantFileTypes
    .filter((fileType) => hasValidDownloadForType(fileType))
    .map((fileType) => {
      const buttonLabel = addCollectionSuffix(fileType, false);
      const tooltipLabel = addCollectionSuffix(fileType, true);

      return {
        buttonText: buttonLabel,
        dataFileType: fileType,
        tooltip: `Download the ${tooltipLabel} for this study`,
      };
    })
    .sort((a, b) => a.dataFileType.localeCompare(b.dataFileType));

  // Hide entire section if no valid downloads
  if (allButtons.length === 0) {
    return null;
  }

  return (
    <Grid item xs={12} className={classes.detailContainerItem}>
      <Grid item container direction="row">
        <Grid item xs={12} className={classes.title}>
          AVAILABLE DOWNLOADS
        </Grid>
        <Grid item xs={12} className={classes.content}>
          Subject to the appropriate access controls, copies of each file type
          pertaining to a study that are currently represented within the
          application can be downloaded in the form of a .zip file by selecting
          the download option(s) below.
          {allButtons.map((btn) => {
            const zipFile = getZipFileForType(btn.dataFileType);
            const hasZip = Boolean(zipFile);

            return (
              <ZipDownloadView
                key={btn.dataFileType}
                disabled={!hasZip}
                fileFormat={hasZip ? zipFile.data_file_format : undefined}
                fileSize={hasZip ? zipFile.data_file_size : undefined}
                fileName={hasZip ? zipFile.data_file_name : undefined}
                fileLocation={hasZip ? zipFile.data_file_uuid : undefined}
                studyAccession={studyAccession}
                toolTipTextFileDownload={
                  hasZip ? btn.tooltip : missingZipTooltip
                }
                iconFileDownload={documentDownloadProps.iconFileDownload}
                iconUnauthenticated={documentDownloadProps.iconUnauthenticated}
                toolTipTextUnauthenticated={
                  documentDownloadProps.toolTipTextUnauthenticated
                }
                toolTipIcon={documentDownloadProps.toolTipIcon}
                buttonText={btn.buttonText}
              />
            );
          })}
        </Grid>
      </Grid>
    </Grid>
  );
};

const styles = (theme) => ({
  detailContainerItem: {
    paddingTop: "30px !important",
  },
  title: {
    color: "#066D93",
    fontFamily: theme.custom.fontFamilyInter,
    fontSize: "18px",
    letterSpacing: "0.017em",
    fontWeight: 500,
    textTransform: "uppercase",
  },
  content: {
    fontSize: "16px",
    fontFamily: theme.custom.fontFamilyNunito,
    marginTop: "8px",
    fontWeight: 400,
    color: "#000",
  },
});

export default withStyles(styles, { withTheme: true })(AvailableDownloads);
